import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, TrendingUp, Compass, Sparkles, Plane, Handshake, Megaphone, BrainCircuit, HeartHandshake, MessageCircle } from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { HeroArt } from "../components/site/HeroArt";
import { HeroCarousel } from "../components/site/HeroCarousel";
import { Marquee } from "../components/site/Marquee";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { VentureCard } from "../components/site/VentureCard";
import { EnquiryForm } from "../components/site/EnquiryForm";
import { api, trackEvent } from "../lib/api";
import { useTitle } from "../App";

const PILLARS = [
  { n: "01", icon: BrainCircuit, title: "AI & Technology", text: "AI products, applications, automation and intelligent platforms — including Media OS and the ventures that follow it.", accent: "text-violet", border: "hover:border-violet/40" },
  { n: "02", icon: TrendingUp, title: "Marketing Intelligence & Performance", text: "Media intelligence, performance marketing and campaign intelligence — AdTech and MarTech thinking applied to real budgets.", accent: "text-teal", border: "hover:border-teal/40" },
  { n: "03", icon: Handshake, title: "Affiliate & Strategic Partnerships", text: "CPS, CPA and CPL programs, publisher partnerships and performance alliances engineered for incrementality.", accent: "text-sky-400", border: "hover:border-sky-400/40" },
  { n: "04", icon: Compass, title: "Consumer Ventures", text: "Travel, hospitality, mobility and everyday services — experience-led businesses built with the same discipline as our technology.", accent: "text-amber-400", border: "hover:border-amber-400/40" },
];

const AI_USES = [
  "Build products", "Automate workflows", "Analyse data", "Generate intelligence",
  "Improve marketing decisions", "Personalise experiences", "Optimise operations", "Reduce manual work",
];

const INTENTS = [
  { key: "grow-my-brand", icon: Megaphone, label: "Grow my brand", text: "You have a product or service that deserves sharper marketing. We start from your offer, audience, objectives and economics — then plan channels, creative and measurement around them.", cta: "Discuss My Growth Plan", to: "/contact?category=grow-my-brand" },
  { key: "digital-product", icon: Sparkles, label: "Explore a digital product", text: "Media OS is live — our AI-powered marketing planning and intelligence product. Turn a brief into a coherent media and creative plan.", cta: "Explore Media OS", to: "/ventures/mediaos" },
  { key: "travel-experience", icon: Plane, label: "Plan a travel experience", text: "Travel Sense brings destination discovery, comparisons and itinerary planning into one connected experience — a more thoughtful way to plan the journey ahead.", cta: "Explore Travel Sense", to: "/ventures/travel-sense" },
  { key: "guidance", icon: HeartHandshake, label: "Personal guidance", text: "Six Senses is a personal guidance companion for reflection, self-discovery and everyday decisions — your Life Saarthi.", cta: "Explore Six Senses", to: "/ventures/six-senses" },
  { key: "partnership", icon: Handshake, label: "Partner with Ekavrix", text: "Brands, publishers, affiliates, technology collaborators and venture partners — if our pillars intersect with your plans, we would like to hear from you.", cta: "Explore Partnership", to: "/partnerships" },
];

const STAGES = [
  { n: "01", title: "Understand", text: "Before anything is planned, we get precise about the offer, the audience, the objective and the economics. Most marketing problems are clarity problems in disguise." },
  { n: "02", title: "Plan", text: "Cohorts, geographies, channels, budgets and creative angles — documented as a plan with reasons attached, not a list of guesses." },
  { n: "03", title: "Execute", text: "Campaigns, content and partnerships launched with tracking in place from day one, so every rupee spent produces learning as well as results." },
  { n: "04", title: "Improve", text: "Regular read-outs against the plan. What works gets scaled deliberately; what does not gets changed or stopped, without sentiment." },
];

export default function Home() {
  useTitle("");
  const [config, setConfig] = useState(null);
  const [ventures, setVentures] = useState([]);
  const [intent, setIntent] = useState(INTENTS[0]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  useEffect(() => {
    api.get("/content/config").then((r) => setConfig(r.data)).catch(() => setConfig({}));
    api.get("/content/ventures").then((r) => setVentures(r.data)).catch(() => {});
  }, []);

  const headline = (config?.hero_headline || "We build AI-powered\nventures that grow brands.").split("\n");
  const waNumber = config?.whatsapp_number;

  return (
    <div data-testid="home-page" className="bg-midnight text-mist">
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="grain relative overflow-hidden pt-[76px]">
        <div className="container-ek grid min-h-[92vh] items-center gap-12 py-16 lg:grid-cols-12">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }} className="eyebrow mb-6" data-testid="hero-eyebrow">
              Ekavrix Tech Ventures · AI-First
            </motion.p>
            <h1 className="font-display text-[2.6rem] font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5rem]">
              {headline.map((line, i) => (
                <MaskedLine key={i} delay={0.25 + i * 0.14}>{line}</MaskedLine>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }} className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg" data-testid="hero-subcopy">
              {config?.hero_subcopy || "Ekavrix Tech Ventures builds AI-powered technology products, intelligent marketing solutions, strategic partnerships and new-age consumer ventures."}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-9 flex flex-wrap gap-4">
              <Link to="/ventures" data-testid="hero-cta-primary" className="btn-primary">
                Explore Our Ventures <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="hero-cta-secondary" className="btn-ghost" onClick={() => trackEvent("contact_click", { from: "hero" })}>
                Work With Ekavrix
              </Link>
              {waNumber && (
                <a href={`https://wa.me/${waNumber}?text=${encodeURIComponent("Hi Ekavrix Team, I would like to discuss a business opportunity.")}`}
                  target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp" onClick={() => trackEvent("whatsapp_click", { from: "hero" })}
                  className="inline-flex items-center gap-2 rounded-full border border-teal/40 px-7 py-3.5 font-display text-sm font-semibold text-teal transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-teal/10">
                  <MessageCircle size={16} /> WhatsApp Us
                </a>
              )}
            </motion.div>
          </motion.div>
          <motion.div style={{ y: artY }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="hidden h-[480px] lg:col-span-5 lg:block xl:h-[560px]">
            <HeroArt />
          </motion.div>
        </div>
      </section>

      <HeroCarousel />

      <Marquee />

      {/* Intro + pillars */}
      <section className="py-24 sm:py-32">
        <div className="container-ek">
          <Reveal>
            <p className="eyebrow mb-4">The Company</p>
            <h2 className="max-w-3xl font-display text-3xl font-semibold tracking-tight sm:text-5xl">One AI-led ecosystem. Four ways it creates value.</h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Ekavrix is an AI-first technology venture company. Each pillar has a distinct purpose, connected by one technology philosophy and a focus on the people each venture serves.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div data-testid={`pillar-${p.n}`} className={`card-dark group h-full p-8 ${p.border}`}>
                  <div className="flex items-center justify-between">
                    <p.icon size={26} className={p.accent} />
                    <span className="font-mono text-xs text-slate-500">{p.n}</span>
                  </div>
                  <h3 className="mt-6 font-display text-lg font-semibold leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI at the Core */}
      <section className="relative overflow-hidden bg-deep py-24 sm:py-32" data-testid="ai-core-section">
        <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-violet/15 blur-[120px]" />
        <div className="pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-teal/10 blur-[120px]" />
        <div className="container-ek relative grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-4">AI at the Core</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-5xl">Not a feature. The foundation.</h2>
            <p className="mt-6 text-base leading-relaxed text-slate-400">
              Ekavrix is built around an AI-first philosophy. From marketing intelligence and automation to consumer products and future ventures, AI forms the technology layer behind how we build and scale.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              Artificial Intelligence sits at the core of how Ekavrix builds, operates, automates and scales its ventures — applied differently in each business, according to what that business actually needs.
            </p>
          </Reveal>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-3">
              {AI_USES.map((u, i) => (
                <Reveal key={u} delay={i * 0.05}>
                  <div className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-midnight/60 px-5 py-4 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-violet/30">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-r from-violet to-teal" />
                    <span className="font-display text-sm font-medium text-slate-200">{u}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Ventures */}
      <section className="py-24 sm:py-32" data-testid="ventures-section">
        <div className="container-ek">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow mb-4">Portfolio</p>
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-5xl">Our Ventures</h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400">
                We're building products and businesses that help people create, make informed decisions and navigate everyday life—from marketing and personal guidance to travel, music, mobility and hospitality.
              </p>
            </div>
            <Link to="/ventures" data-testid="ventures-view-all" className="group flex items-center gap-2 text-sm font-medium text-teal transition-colors hover:text-teal-hover">
              Explore in detail <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ventures.map((v, i) => <VentureCard key={v.slug} venture={v} index={i} />)}
          </div>
        </div>
      </section>

      {/* Marketing Intelligence — light editorial */}
      <section className="bg-cream py-24 text-slatetext sm:py-32">
        <div className="container-ek grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-4 !text-violet">Marketing Intelligence & Performance</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-midnight sm:text-5xl">A clearer plan. Stronger creative. Better decisions.</h2>
            <p className="mt-6 text-base leading-relaxed text-slatetext/75">
              Good marketing does not start with channels. It starts with the brand's offering, its audience, the business objective and the economics behind both. Ekavrix builds strategy, acquisition, affiliate partnerships, creative and measurement on that foundation.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/solutions" data-testid="solutions-cta" className="btn-dark">Explore Solutions <ArrowRight size={15} /></Link>
              <Link to="/affiliate" data-testid="affiliate-cta" className="inline-flex items-center gap-2 rounded-full border border-midnight/15 px-7 py-3.5 font-display text-sm font-semibold text-midnight transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-midnight/5">
                Affiliate Partnerships <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            {[
              ["Strategy", "Audience cohorts, market prioritisation and media planning tied to business objectives."],
              ["Acquisition", "Paid search, shopping, social and app campaigns run against CAC and ROAS, not impressions."],
              ["Partnerships", "Affiliate and strategic partner programmes with validation, structure and quality control."],
              ["Creative", "Message angles and channel-specific copy grounded in the actual offer, tested systematically."],
              ["Measurement", "Tracking plans and reporting built to produce decisions, not decorate dashboards."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="group flex items-baseline gap-6 border-b border-midnight/[0.08] py-6 transition-[padding] duration-300 hover:pl-3">
                  <span className="font-mono text-xs text-violet">0{i + 1}</span>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-midnight sm:text-xl">{t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slatetext/70">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Intent selector */}
      <section className="py-24 sm:py-32">
        <div className="container-ek">
          <Reveal className="text-center">
            <p className="eyebrow mb-4">Start Here</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-5xl">What brings you to Ekavrix?</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mx-auto mt-12 flex max-w-4xl flex-wrap justify-center gap-3" role="tablist" aria-label="Enquiry intents">
              {INTENTS.map((it) => (
                <button key={it.key} data-testid={`intent-${it.key}`} role="tab" aria-selected={intent.key === it.key}
                  onClick={() => setIntent(it)}
                  className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-display text-sm font-medium transition-[background-color,border-color,color,transform] duration-300 hover:-translate-y-0.5 ${intent.key === it.key ? "border-violet bg-violet text-white shadow-[0_10px_30px_rgba(118,87,232,0.35)]" : "border-white/12 text-slate-300 hover:border-violet/50 hover:text-white"}`}>
                  <it.icon size={15} /> {it.label}
                </button>
              ))}
            </div>
          </Reveal>
          <motion.div key={intent.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            data-testid="intent-panel" className="card-dark mx-auto mt-8 max-w-3xl p-8 text-center sm:p-10">
            <p className="mx-auto max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">{intent.text}</p>
            <Link to={intent.to} data-testid="intent-cta" className="btn-primary mt-7">
              {intent.cta} <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Working approach — light manifesto */}
      <section className="bg-cream py-24 text-slatetext sm:py-32">
        <div className="container-ek">
          <Reveal>
            <p className="eyebrow mb-4 !text-violet">How We Work</p>
            <h2 className="max-w-2xl font-display text-3xl font-semibold tracking-tight text-midnight sm:text-5xl">Four stages. No theatre.</h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-midnight/[0.08] bg-midnight/[0.08] sm:grid-cols-2 lg:grid-cols-4">
            {STAGES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08} className="bg-cream">
                <div data-testid={`stage-${s.n}`} className="group h-full p-8 transition-[background-color] duration-500 hover:bg-white">
                  <span className="font-display text-5xl font-bold text-midnight/[0.08] transition-colors duration-500 group-hover:text-violet/25">{s.n}</span>
                  <h3 className="mt-5 font-display text-xl font-semibold text-midnight">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slatetext/70">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder teaser */}
      <section className="py-24 sm:py-32">
        <div className="container-ek grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-violet/25 to-teal/20 blur-2xl" />
              <div className="relative flex aspect-[4/5] items-center justify-center overflow-hidden rounded-[1.6rem] border border-white/10 bg-gradient-to-br from-cardblue to-deep">
                {config?.founder_photo ? (
                  <img src={config.founder_photo} alt={`${config?.founder_name || "Deepak Chanchal"}, ${config?.founder_role || "Founder & Business Lead"} of Ekavrix`} className="absolute inset-0 h-full w-full object-cover object-top" />
                ) : (
                  <span className="font-display text-7xl font-bold tracking-tight text-white/90">
                    {(config?.founder_name || "Deepak Chanchal").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 to-transparent" />
                <span className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">{config?.founder_role || "Founder & Business Lead"}</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p className="eyebrow mb-4">Leadership</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{config?.founder_name || "Deepak Chanchal"}</h2>
            <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-violet">{config?.founder_role || "Founder & Business Lead"}</p>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400">{config?.founder_bio}</p>
            <Link to="/about" data-testid="founder-more" className="group mt-7 inline-flex items-center gap-2 text-sm font-medium text-teal transition-colors hover:text-teal-hover">
              Read the full story <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Partnership invitation */}
      <section className="relative overflow-hidden bg-deep py-24 sm:py-28">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-violet/15 blur-[110px]" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-teal/10 blur-[110px]" />
        <div className="container-ek relative">
          <Reveal className="mx-auto max-w-3xl text-center">
            <p className="eyebrow mb-4">Partnerships</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-5xl">Building something adjacent?</h2>
            <p className="mt-6 text-base leading-relaxed text-slate-400">
              We welcome conversations with brands, founders, publishers, affiliate partners and venture collaborators whose plans intersect with ours. No decks required — a clear note is enough to start.
            </p>
            <Link to="/partnerships" data-testid="partnership-cta" className="btn-ghost mt-9">
              Start a Partnership Conversation <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Final enquiry */}
      <section className="grain relative py-24 sm:py-32">
        <div className="container-ek grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-4">Enquire</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-5xl">Let's make your next move meaningful.</h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
              Tell us where you are headed. Every enquiry is read personally, saved to our system and answered with a considered reply — usually within two working days.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="card-dark p-7 sm:p-10">
              <EnquiryForm title="Tell us about it" sourcePage="/" />
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
